// Screen-space helpers of the site chips, free of the DOM so they can be tested.
//
//   Thinner        one chip per screen cell, so a crowded region shows a few chips, not a carpet
//   nearestTarget  which chip or model the pointer is on
//   sameBoxes      whether the banners the chips dodge have moved

/** Where the pointer counts as on a site: a centre and a radius, in px. */
export interface Target {
  key: string;
  x: number;
  y: number;
  r: number;
}

const KEY_STRIDE = 4096;
const KEY_OFFSET = 2048;

/** A grid over the screen that lets one chip take each cell. */
export class Thinner {
  private readonly cell: number;
  private readonly taken = new Set<number>();

  constructor(cell: number) {
    this.cell = cell;
  }

  /** True, and the cell is now taken, when no chip holds the cell of (x, y). */
  take(x: number, y: number): boolean {
    const key = (Math.floor(x / this.cell) + KEY_OFFSET) * KEY_STRIDE + Math.floor(y / this.cell) + KEY_OFFSET;
    if (this.taken.has(key)) {
      return false;
    }

    this.taken.add(key);
    return true;
  }

  reset(): void {
    this.taken.clear();
  }
}

/** The target nearest to (x, y) among those whose radius holds it; null when none does. */
export function nearestTarget(targets: readonly Target[], x: number, y: number): Target | null {
  let best: Target | null = null;
  let bestD = Infinity;
  for (const t of targets) {
    const d = Math.hypot(t.x - x, t.y - y);
    if (d > t.r || d >= bestD) {
      continue;
    }

    best = t;
    bestD = d;
  }
  return best;
}

/** A screen rect: left, top, right, bottom, in px. */
export type Box = readonly [number, number, number, number];

const SAME_BOX_TOL_PX = 0.5;

/** Whether two lists hold the same rects, in order, give or take a sub-pixel. */
export function sameBoxes(a: readonly Box[], b: readonly Box[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < 4; j++) {
      if (Math.abs(a[i][j] - b[i][j]) > SAME_BOX_TOL_PX) {
        return false;
      }
    }
  }
  return true;
}
